import type { BundlePdpResponse } from "@halm-club/halm-shared";
import { useState } from "react";
import { VideoReviews } from "./VideoReviews";
import "./ReviewSection.css";

type ReviewContent = NonNullable<BundlePdpResponse["reviewContent"]>;

type ReviewSectionProps = {
  buyerCount?: number;
  reviewContent?: ReviewContent;
};

const getRandomBuyerCount = () =>
  Math.floor(Math.random() * (1600 - 1400 + 1)) + 1400;

const getRoundedReviewCount = (numberOfReviews: number) =>
  `${Math.floor(Math.max(numberOfReviews, 0) / 100) * 100}+`;

const getStarCount = (numberOfStars: number) =>
  Math.min(Math.max(Math.round(numberOfStars), 0), 5);

export function ReviewSection({
  buyerCount,
  reviewContent,
}: ReviewSectionProps) {
  const [fallbackBuyerCount] = useState(getRandomBuyerCount);
  const displayedBuyerCount = buyerCount ?? fallbackBuyerCount;

  if (!reviewContent) return null;

  const {
    featuredReview,
    linkToReviews,
    numberOfReviews,
    rating,
    videoReviews = [],
  } = reviewContent;
  const hasReviewSummary =
    typeof rating === "number" && typeof numberOfReviews === "number";
  const hasVideoReviews = videoReviews.length > 0;

  if (!hasReviewSummary && !hasVideoReviews && !featuredReview) return null;

  const isFeaturedReviewOnly = !hasReviewSummary && !hasVideoReviews;

  return (
    <section
      className={`react-pdp__onetime-reviews${
        isFeaturedReviewOnly
          ? " react-pdp__onetime-reviews--featured-review-only"
          : ""
      }`}
      {...(hasReviewSummary
        ? { "aria-labelledby": "react-pdp-reviews-heading" }
        : { "aria-label": "Kundenbewertungen" })}
    >
      {hasReviewSummary ? (
        <div className="react-pdp__onetime-reviews-intro">
          <h2
            className="react-pdp__onetime-reviews-heading"
            id="react-pdp-reviews-heading"
          >
            Echte Kunden,{" "}
            <span className="react-pdp__onetime-reviews-heading-accent">
              echte Ergebnisse.
            </span>
          </h2>
          <p className="react-pdp__onetime-reviews-description">
            <strong>{displayedBuyerCount} Käufer</strong> in den letzten 30
            Tagen vertrauen Halm.
          </p>

          <div
            className="react-pdp__onetime-review-benefits"
            aria-label="Bewertungen und Vorteile"
          >
            <div className="react-pdp__onetime-review-benefit">
              <span
                aria-hidden="true"
                className="material-symbols-rounded filled-icon react-pdp__onetime-review-benefit-icon react-pdp__onetime-review-benefit-icon--rating"
              >
                hotel_class
              </span>
              <strong>{rating}</strong>
              <span aria-hidden="true">·</span>
              <span>{getRoundedReviewCount(numberOfReviews)}</span>
            </div>
            <div className="react-pdp__onetime-review-benefit">
              <span
                aria-hidden="true"
                className="material-symbols-rounded react-pdp__onetime-review-benefit-icon react-pdp__onetime-review-benefit-icon--protection"
              >
                verified_user
              </span>
              <strong>Käuferschutz</strong>
            </div>
            <div className="react-pdp__onetime-review-benefit">
              <span
                aria-hidden="true"
                className="material-symbols-rounded react-pdp__onetime-review-benefit-icon react-pdp__onetime-review-benefit-icon--location"
              >
                location_on
              </span>
              <strong>Made in DE</strong>
            </div>
          </div>
        </div>
      ) : null}

      {hasVideoReviews ? <VideoReviews videoReviews={videoReviews} /> : null}

      {featuredReview ? (
        <div className="react-pdp__onetime-featured-review mobile">
          {featuredReview.heading ? (
            <div className="react-pdp__onetime-review-label">
              <span
                aria-hidden="true"
                className="material-symbols-rounded react-pdp__onetime-review-label-icon"
              >
                chat
              </span>
              <h3>{featuredReview.heading}</h3>
            </div>
          ) : null}

          <article className="react-pdp__onetime-review-card">
            <div className="react-pdp__onetime-review-author-row">
              {featuredReview.imageSource ? (
                <img
                  alt=""
                  className="react-pdp__onetime-review-image"
                  src={featuredReview.imageSource}
                />
              ) : null}
              <div className="react-pdp__onetime-review-author">
                <div className="react-pdp__onetime-review-author-name">
                  <strong>{featuredReview.author}</strong>
                  <span
                    aria-label="Verifizierte Bewertung"
                    className="react-pdp__onetime-review-verified"
                    role="img"
                  >
                    <span
                      aria-hidden="true"
                      className="material-symbols-rounded"
                    >
                      check
                    </span>
                  </span>
                </div>
                <span className="react-pdp__onetime-review-place">
                  {featuredReview.place}
                </span>
                <div
                  className="react-pdp__onetime-review-stars"
                  aria-label={`${getStarCount(featuredReview.numberOfStars)} von 5 Sternen`}
                >
                  {Array.from(
                    { length: getStarCount(featuredReview.numberOfStars) },
                    (_, index) => (
                      <span
                        aria-hidden="true"
                        className="material-symbols-rounded filled-icon"
                        key={index}
                      >
                        star
                      </span>
                    ),
                  )}
                </div>
              </div>
            </div>
            <p className="react-pdp__onetime-review-quote">
              “{featuredReview.reviewContent}”
            </p>
          </article>

          {linkToReviews && numberOfReviews != null ? (
            <a
              className="react-pdp__onetime-reviews-link"
              href={linkToReviews}
              target="_blank"
            >
              Alle {numberOfReviews} Halm Bewertungen ansehen →
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
